import { imax, imin, mulBp, withinRange, iatan2 } from '../math/fixed.ts';
import type { BattleState, Enemy } from '../types.ts';
import type { HowlerState, Pt, TrailRules, TrailState } from '../caza-types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The howler (docs/sendas/barbaro.md, chapter 1, act 3): a big wolf at the head of the pack on the ledge, standing in
 * the trail's way. Every `every` it rises on its hind legs and bristles (`rear`, 3 s: it stands still, the warning),
 * then howls (`howl`, 2.5 s). Struck for `cutBp` of its life in the howl, or hit by a loose stone rearing or howling,
 * the howl is cut and it reels; let out, the wolves fallen since the last howl get up (`raise` at most, where they fell)
 * and the whole pack heals `healBp` of its life. It bites `biteBp` of the hero's life, its own blow.
 *
 * Cost: one wolf looked at once a tick; the pack it heals is the act's list (a handful).
 */

const fresh = (s: BattleState, r: TrailRules): HowlerState => {
  const H = r.howler!;
  // Ahead on the way: by the second crossing's middle, in the trail's way.
  const c = r.crossings[imin(1, r.crossings.length - 1)];
  const at = c ? c.at : r.end;
  const e = spawnEnemy(s, 'wolf', 1, false, 0, at.x, at.y + 600, H.hp);
  return { id: e.id, next: H.first, phase: 0, until: 0, hpAt: 0, cuts: 0, howls: 0, cd: 0, away: 0, bites: 0 };
};

/** The howler, if it still stands. */
export const howlerOf = (s: BattleState): Enemy | undefined => {
  const st = s.senda?.howler;
  return st ? s.enemies.find((e) => e.id === st.id && e.hp > 0) : undefined;
};

/** The howl cut (struck enough, or a stone on it): it reels, and the next comes later. */
export const howlCut = (s: BattleState, e: Enemy, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.trail;
  const st = s.senda?.howler;
  if (!r?.howler || !st || st.phase === 0 || st.id !== e.id) return;
  st.phase = 0;
  st.next = s.waveT + r.howler.every;
  st.cuts++;
  e.stun = imax(e.stun, r.howler.stun);
  ev(s, { t: 'senda', kind: 'cutHowl', npc: '', x: e.x, y: e.y, n: st.cuts });
  cue(`cut${st.cuts}`);
};

/** The howl let out: the fallen get up where they fell (the newest first), and the pack heals. */
const howled = (s: BattleState, r: TrailRules, w: TrailState, st: HowlerState, e: Enemy, cue: (c: string) => void): void => {
  const H = r.howler!;
  st.phase = 0;
  st.next = s.waveT + H.every;
  st.howls++;
  ev(s, { t: 'senda', kind: 'howled', npc: '', x: e.x, y: e.y, n: st.howls });
  cue(`howled${st.howls}`);
  for (let k = 0; k < H.raise && w.fallen.length; k++) {
    const p = w.fallen.pop() as Pt;
    const up = spawnEnemy(s, 'wolf', 1, false, 0, p.x, p.y, r.wolfHp);
    w.pack.push(up.id);
    ev(s, { t: 'senda', kind: 'rise', npc: '', x: p.x, y: p.y });
  }
  w.fallen.length = 0;
  for (const id of [st.id, ...w.pack]) {
    const o = s.enemies.find((x) => x.id === id && x.hp > 0);
    if (o) o.hp = imin(o.maxHp, o.hp + mulBp(o.maxHp, H.healBp));
  }
};

export const howlerTick = (s: BattleState, r: TrailRules, w: TrailState, cue: (c: string) => void): void => {
  const H = r.howler;
  const sd = s.senda;
  if (!H || !sd) return;
  const st = (sd.howler ??= fresh(s, r));
  const e = s.enemies.find((x) => x.id === st.id);
  if (!e || e.hp <= 0) {
    if (st.phase !== 3) {
      st.phase = 3;
      cue('howlerDown');
    }
    return;
  }
  if (w.done >= 0) return;
  if (st.cd > 0) st.cd--;
  if (st.phase === 0 && s.waveT >= st.next && e.stun === 0) {
    st.phase = 1;
    st.until = s.tick + H.rear;
    ev(s, { t: 'senda', kind: 'rear', npc: '', x: e.x, y: e.y, n: H.rear });
    cue('rear1');
    return;
  }
  if (st.phase === 1 && s.tick >= st.until) {
    st.phase = 2;
    st.until = s.tick + H.howl;
    st.hpAt = e.hp;
    ev(s, { t: 'senda', kind: 'howl', npc: '', x: e.x, y: e.y, n: H.howl });
    return;
  }
  if (st.phase === 2) {
    if (st.hpAt - e.hp >= mulBp(e.maxHp, H.cutBp)) return howlCut(s, e, cue);
    if (s.tick >= st.until) howled(s, r, w, st, e, cue);
  }
};

/** The howler moves and bites by its own rule; rearing and howling it stands (true: the act moved it). */
export const howlerHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const st = s.senda?.howler;
  const H = s.rules?.senda?.trail?.howler;
  if (!st || !H || st.id !== e.id || e.stun > 0) return false;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (st.phase === 1 || st.phase === 2) {
    e.anim = st.phase === 1 ? 'cast' : 'attack';
    if (h && (h.x !== e.x || h.y !== e.y)) e.facing = iatan2(h.y - e.y, h.x - e.x);
    return true;
  }
  if (!h) {
    e.anim = 'idle';
    return true;
  }
  // Bitten: it backs off up the trail, ahead of the hero (in its way), and keeps there a while.
  if (s.tick < st.away) {
    const w = s.senda?.trail;
    const r = s.rules?.senda?.trail;
    const ahead = r && w ? (r.crossings[w.at + 1]?.at ?? r.end) : null;
    const far = withinRange(e.x, e.y, h.x, h.y, H.keep);
    if (far && ahead && !withinRange(ahead.x, ahead.y, h.x, h.y, H.keep)) stepToward(e, ahead.x, ahead.y, px(e));
    else if (far) stepToward(e, e.x + (e.x - h.x), e.y + (e.y - h.y), px(e));
    e.anim = far ? 'walk' : 'idle';
    if (!far && (h.x !== e.x || h.y !== e.y)) e.facing = iatan2(h.y - e.y, h.x - e.x);
    return true;
  }
  if (!withinRange(e.x, e.y, h.x, h.y, H.reach)) {
    e.anim = 'walk';
    stepToward(e, h.x, h.y, px(e));
    return true;
  }
  if (h.x !== e.x || h.y !== e.y) e.facing = iatan2(h.y - e.y, h.x - e.x);
  if (st.cd === 0) {
    st.cd = H.reload;
    e.anim = 'attack';
    e.animT = 0;
    damageHero(s, h, mulBp(h.stats.maxHp, H.biteBp), e, 'physical', false);
    st.bites++;
    if (st.bites % H.bites === 0) st.away = s.tick + H.back;
  } else if (e.anim !== 'attack' || e.animT > 6) e.anim = 'idle';
  return true;
};
